import React, { useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Document, Page, pdfjs } from 'react-pdf';
import { X, ZoomIn, ZoomOut, ShieldCheck, EyeOff } from 'lucide-react';
import api from '../../services/api';
import { useAuth } from '../../contexts/AuthContext';
import LoadingSpinner from './LoadingSpinner';

pdfjs.GlobalWorkerOptions.workerSrc = new URL(
  'pdfjs-dist/build/pdf.worker.min.mjs',
  import.meta.url,
).toString();

const MIN_ZOOM = 0.6;
const MAX_ZOOM = 2;
const ZOOM_STEP = 0.2;
// Keys that would save, print or copy the page
const BLOCKED_KEYS = new Set(['s', 'p', 'c', 'a', 'u']);

/**
 * Tiles the viewer's identity diagonally across a rendered page. It is painted
 * into the page canvas itself, so it cannot be removed by deleting a DOM element,
 * and any screenshot or photo that leaks shows who it came from.
 */
const drawWatermark = (canvas, text) => {
  const ctx = canvas?.getContext('2d');
  if (!ctx) return;

  const fontSize = Math.max(Math.round(canvas.width / 38), 12);
  ctx.save();
  ctx.setTransform(1, 0, 0, 1, 0, 0);
  ctx.font = `600 ${fontSize}px sans-serif`;
  ctx.fillStyle = 'rgba(100, 116, 139, 0.20)';
  ctx.textAlign = 'center';
  ctx.textBaseline = 'middle';
  ctx.translate(canvas.width / 2, canvas.height / 2);
  ctx.rotate(-Math.PI / 6);

  const stepX = ctx.measureText(text).width + fontSize * 4;
  const stepY = fontSize * 7;
  const reach = Math.hypot(canvas.width, canvas.height) / 2;
  for (let y = -reach, row = 0; y <= reach; y += stepY, row += 1) {
    // Stagger alternate rows so the text forms a brick pattern
    const offset = row % 2 ? stepX / 2 : 0;
    for (let x = -reach - offset; x <= reach; x += stepX) {
      ctx.fillText(text, x, y);
    }
  }
  ctx.restore();
};

/**
 * View-only policy reader. Pages are drawn to canvas with no text or annotation
 * layer, and there is no download/print control. The PDF bytes are fetched with
 * the auth token, so the file has no URL that can be opened or shared directly.
 */
const PolicyViewer = ({ policy, onClose }) => {
  const { user } = useAuth();
  const [fileData, setFileData] = useState(null);
  const [error, setError] = useState('');
  const [numPages, setNumPages] = useState(0);
  const [zoom, setZoom] = useState(1);
  const [containerWidth, setContainerWidth] = useState(0);
  // Content is hidden whenever a screenshot looks likely; the viewer clicks to resume
  const [hidden, setHidden] = useState(false);
  const containerRef = useRef(null);
  const pageCanvases = useRef({});

  // Fixed when the viewer opens, so every page carries the same stamp
  const watermarkText = useMemo(() => {
    const openedAt = new Date().toLocaleString('en-IN', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      hour12: true,
    });
    return [user?.name, user?.employeeId || user?.email, openedAt].filter(Boolean).join(' • ');
  }, [user]);

  useEffect(() => {
    let cancelled = false;
    setFileData(null);
    setError('');
    setNumPages(0);

    api
      .get(`/policies/${policy._id}/file`, { responseType: 'arraybuffer', timeout: 120000 })
      .then((response) => {
        if (!cancelled) setFileData(new Uint8Array(response.data));
      })
      .catch(() => {
        if (!cancelled) setError('Failed to load the policy. Please try again.');
      });

    return () => {
      cancelled = true;
    };
  }, [policy._id]);

  // react-pdf reloads whenever the file prop changes identity, so keep it stable
  const file = useMemo(() => (fileData ? { data: fileData } : null), [fileData]);

  useEffect(() => {
    const handleKeyDown = (event) => {
      if (event.key === 'Escape') {
        onClose();
        return;
      }
      // Every macOS screenshot shortcut starts with Cmd+Shift (3, 4, 5); the OS
      // swallows the digit, but the page still sees the modifiers first
      if (event.metaKey && event.shiftKey) {
        setHidden(true);
      }
      if (event.key === 'PrintScreen') {
        setHidden(true);
      }
      if ((event.ctrlKey || event.metaKey) && BLOCKED_KEYS.has(event.key.toLowerCase())) {
        event.preventDefault();
        event.stopPropagation();
      }
    };
    // Windows often only reports PrintScreen on keyup, after it copied the screen
    const handleKeyUp = (event) => {
      if (event.key === 'PrintScreen') {
        setHidden(true);
        navigator.clipboard?.writeText('').catch(() => {});
      }
    };
    // Snipping Tool, Win+Shift+S and other capture apps take focus from the browser
    const hide = () => setHidden(true);
    const handleVisibilityChange = () => {
      if (document.hidden) hide();
    };

    window.addEventListener('keydown', handleKeyDown, true);
    window.addEventListener('keyup', handleKeyUp, true);
    window.addEventListener('blur', hide);
    document.addEventListener('visibilitychange', handleVisibilityChange);
    document.body.classList.add('policy-viewer-open');

    return () => {
      window.removeEventListener('keydown', handleKeyDown, true);
      window.removeEventListener('keyup', handleKeyUp, true);
      window.removeEventListener('blur', hide);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      document.body.classList.remove('policy-viewer-open');
    };
  }, [onClose]);

  useEffect(() => {
    const element = containerRef.current;
    if (!element) return undefined;
    const observer = new ResizeObserver(([entry]) => {
      setContainerWidth(entry.contentRect.width);
    });
    observer.observe(element);
    return () => observer.disconnect();
  }, []);

  const pageWidth = Math.max(Math.min(containerWidth - 32, 900), 280) * zoom;

  // Portal to <body>: the portal layouts animate with `transform`, which would
  // otherwise trap this fixed overlay inside the content card.
  return createPortal(
    <div
      className="fixed inset-0 z-[60] flex flex-col bg-slate-900/90 backdrop-blur-sm select-none"
      onContextMenu={(event) => event.preventDefault()}
      onDragStart={(event) => event.preventDefault()}
    >
      <div className="flex items-center justify-between gap-3 border-b border-slate-700 bg-slate-900 px-4 py-3 text-white">
        <div className="min-w-0">
          <h2 className="truncate text-base font-semibold">{policy.title}</h2>
          <p className="flex items-center gap-1 text-xs text-slate-400">
            <ShieldCheck className="h-3.5 w-3.5" />
            View only{numPages ? ` · ${numPages} ${numPages === 1 ? 'page' : 'pages'}` : ''}
          </p>
        </div>

        <div className="flex shrink-0 items-center gap-2">
          <button
            type="button"
            onClick={() => setZoom((z) => Math.max(MIN_ZOOM, +(z - ZOOM_STEP).toFixed(1)))}
            disabled={zoom <= MIN_ZOOM}
            className="rounded-lg p-2 text-slate-300 hover:bg-slate-800 hover:text-white disabled:opacity-40"
            title="Zoom out"
          >
            <ZoomOut className="h-5 w-5" />
          </button>
          <span className="w-12 text-center text-sm text-slate-300">{Math.round(zoom * 100)}%</span>
          <button
            type="button"
            onClick={() => setZoom((z) => Math.min(MAX_ZOOM, +(z + ZOOM_STEP).toFixed(1)))}
            disabled={zoom >= MAX_ZOOM}
            className="rounded-lg p-2 text-slate-300 hover:bg-slate-800 hover:text-white disabled:opacity-40"
            title="Zoom in"
          >
            <ZoomIn className="h-5 w-5" />
          </button>
          <button
            type="button"
            onClick={onClose}
            className="ml-2 rounded-lg p-2 text-slate-300 hover:bg-slate-800 hover:text-white"
            title="Close"
          >
            <X className="h-5 w-5" />
          </button>
        </div>
      </div>

      <div ref={containerRef} className="relative flex-1 overflow-auto">
        {hidden && (
          <button
            type="button"
            onClick={() => setHidden(false)}
            className="fixed inset-x-0 bottom-0 top-[65px] z-10 flex flex-col items-center justify-center gap-3 bg-slate-900 text-slate-300"
          >
            <EyeOff className="h-10 w-10" />
            <span className="text-base font-medium">Policy hidden</span>
            <span className="text-sm text-slate-400">Click anywhere to continue reading</span>
          </button>
        )}
        <div className={hidden ? 'invisible' : undefined}>
        {error ? (
          <p className="mt-16 text-center text-sm text-red-300">{error}</p>
        ) : !file ? (
          <div className="mt-16">
            <LoadingSpinner size="lg" className="mx-auto" />
          </div>
        ) : (
          <Document
            file={file}
            onLoadSuccess={({ numPages: total }) => setNumPages(total)}
            onLoadError={() => setError('This policy could not be opened.')}
            loading={<div className="mt-16"><LoadingSpinner size="lg" className="mx-auto" /></div>}
            className="flex flex-col items-center gap-4 py-6"
          >
            {Array.from({ length: numPages }, (_, index) => (
              <Page
                key={index + 1}
                pageNumber={index + 1}
                width={pageWidth}
                canvasRef={(canvas) => {
                  pageCanvases.current[index + 1] = canvas;
                }}
                onRenderSuccess={() => drawWatermark(pageCanvases.current[index + 1], watermarkText)}
                renderTextLayer={false}
                renderAnnotationLayer={false}
                className="shadow-2xl"
              />
            ))}
          </Document>
        )}
        </div>
      </div>
    </div>,
    document.body,
  );
};

export default PolicyViewer;
